import { useSelector } from "react-redux";
import { Button } from "semantic-ui-react";
import { selectAllResourceChanges } from "../../slices/resourceChangeReport/resourceChangeReportSlice";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../../slices/currentUser/currentUserSlice";
import getDisplayTitle from "../resourceDisplayCommonFunctions/getDisplayTitle";
import { selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { displayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRPeriod } from "../../datatypeComponents/PeriodEntry";
import { displayStringFromFHIRCoding } from "../../datatypeComponents/CodingEntry";
import { displayStringFromFHIRExpression } from "../../datatypeComponents/ExpressionEntry";
import { displayStringFromFHIRQuantity } from "../../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../../datatypeComponents/RangeEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import { displayStringFromFHIRRelativeTime } from "../../datatypeComponents/RelativeTimeEntry";
import submitFhirJson from "../../functions/submitFhirJsonAPI";

const createChangeReport = async (userName, resourceJson, parentResourceDetail, resourceChanges, apiToken) => {
    let changeReportJson = {
        "resourceType": "ArtifactAssessment",
        "meta": { "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/adaptation"] },
        "extension": [
            {
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-status",
                "valueCode": "active"
            },
            {
                "url": "http://hl7.org/fhir/StructureDefinition/artifact-author",
                "valueContactDetail": { "name": userName }
            }
        ],
        "title": userName + "'s Change Report for " + getDisplayTitle(resourceJson, parentResourceDetail),
        "artifactReference": {
            "reference": resourceJson.resourceType + "/" + resourceJson.id,
            "type": resourceJson.resourceType,
            "display": getDisplayTitle(resourceJson, parentResourceDetail)
        },
        "date": new Date().toISOString(),
        "content": []
    };
    for (const change of resourceChanges) {
        let originalValue = "";
        if (change.originalValue !== null && change.originalValue !== undefined) {
            if (typeof change.originalValue === "object") {
                originalValue = JSON.stringify(change.originalValue);
                if (change.originalDatatype === "CodeableConcept") {
                    originalValue = displayStringFromFHIRCodeableConcept(change.originalValue);
                } else if (change.originalDatatype === "Period") {
                    originalValue = displayStringFromFHIRPeriod(change.originalValue);
                } else if (change.originalDatatype === "Coding") {
                    originalValue = displayStringFromFHIRCoding(change.originalValue);
                } else if (change.originalDatatype === "Expression") {
                    originalValue = displayStringFromFHIRExpression(change.originalValue);
                } else if (change.originalDatatype === "Quantity") {
                    originalValue = displayStringFromFHIRQuantity(change.originalValue);
                } else if (change.originalDatatype === "Range") {
                    originalValue = displayStringFromFHIRRange(change.originalValue);
                } else if (change.originalDatatype === "Reference") {
                    originalValue = displayStringFromFHIRReference(change.originalValue);
                } else if (change.originalDatatype === "RelativeTime") {
                    originalValue = displayStringFromFHIRRelativeTime(change.originalValue);
                }
            } else if (typeof change.originalValue === "string") {
                originalValue = change.originalValue;
            } else {
                originalValue = change.originalValue.toString();
            }
        }
        let newValue = "";
        if (change.newValue !== null && change.newValue !== undefined) {
            if (typeof change.newValue === "object") {
                newValue = JSON.stringify(change.newValue)
                if (change.newDatatype === "CodeableConcept") {
                    newValue = displayStringFromFHIRCodeableConcept(change.newValue);
                } else if (change.newDatatype === "Period") {
                    newValue = displayStringFromFHIRPeriod(change.newValue);
                } else if (change.newDatatype === "Coding") {
                    newValue = displayStringFromFHIRCoding(change.newValue);
                } else if (change.newDatatype === "Expression") {
                    newValue = displayStringFromFHIRExpression(change.newValue);
                } else if (change.newDatatype === "Quantity") {
                    newValue = displayStringFromFHIRQuantity(change.newValue);
                } else if (change.newDatatype === "Range") {
                    newValue = displayStringFromFHIRRange(change.newValue);
                } else if (change.newDatatype === "Reference") {
                    newValue = displayStringFromFHIRReference(change.newValue);
                } else if (change.newDatatype === "RelativeTime") {
                    newValue = displayStringFromFHIRRelativeTime(change.newValue);
                }
            } else if (typeof change.newValue === "string") {
                newValue = change.newValue;
            } else {
                newValue = change.newValue.toString();
            }
        }
        let contentEntry = {
            "type": { "text": "adapted element" },
            "classifier": [{ "text": change.originalDatatype }],
            "path": ["Resource FOI " + change.resourceId, change.originalPath],
            "component": [
                {
                    "summary": originalValue,
                    "type": {
                        "coding": [
                            {
                                "system": "https://fevir.net/resources/CodeSystem/179423",
                                "code": "OriginalResourceContent",
                                "display": "Original Resource Content"
                            }
                        ]
                    }
                },
                {
                    "summary": newValue,
                    "type": {
                        "coding": [
                            {
                                "system": "https://fevir.net/resources/CodeSystem/179423",
                                "code": "SuggestedChange",
                                "display": "Suggested Change to Resource Content"
                            }
                        ]
                    }
                }
            ]
        };
        changeReportJson.content.push(contentEntry);
    }
    let response = await submitFhirJson(changeReportJson, changeReportJson.title, apiToken);
    let resourceFOI;
    if (response?.success) {
        resourceFOI = response.formstateid;
        alert("Change Report created -- view at https://fevir.net/" + resourceFOI);
    }
}

const CreateChangeReportButton = ({ foi }) => {

    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const apiToken = useSelector(selectCurrentUserApiToken);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson || parentResourceDetail?.resourceJson;
    const userName = useSelector(selectCurrentUserName);

    return <Button className="formButton navigationButton"
        style={{ color: "#000000", width: "97%", margin: "0px" }}
        content="Create Change Report"
        onClick={() => {
            createChangeReport(userName, resourceJson, parentResourceDetail, resourceChanges,
                apiToken
            );
        }}
        disabled={resourceChanges.length === 0} />;
}

export default CreateChangeReportButton;